/**
 * Guía paso a paso de cada papel (Robert, 02/11/2026: «un botón “Ver guía” y que salga guiado,
 * explicando qué es cada cosa»). Cada paso puede llevar a una pantalla (`ruta`) y resaltar una
 * zona (`selector`, el primer elemento visible con esa marca `data-guia`); si no la encuentra
 * (pantalla de móvil, lista vacía…), la explicación sale en el centro. Los textos, en
 * `nevora.guia.<papel>.<paso>.titulo|texto`.
 */
export type PapelGuia = "setter" | "consultor";

export type PasoGuia = {
  id: string;
  ruta?: string;
  selector?: string;
  /** Dibujo de apoyo en la tarjeta (los botones de la ficha). */
  ilustracion?: "resultados" | "consultoria";
};

const marca = (...ids: string[]) =>
  ids.map((id) => `[data-guia="${id}"]`).join(", ");

export const PASOS_GUIA: Record<PapelGuia, PasoGuia[]> = {
  setter: [
    { id: "bienvenida", ruta: "/" },
    { id: "inicio", ruta: "/", selector: marca("inicio-cifras") },
    { id: "alertas", ruta: "/", selector: marca("inicio-alertas") },
    { id: "leads", ruta: "/negocios", selector: marca("leads-lista") },
    { id: "ficha", ruta: "/negocios" },
    { id: "resultado", ruta: "/negocios", ilustracion: "resultados" },
    { id: "pipeline", ruta: "/pipeline", selector: marca("pipeline-tablero") },
    { id: "agenda", ruta: "/agenda", selector: marca("agenda") },
    { id: "buscar", selector: marca("buscar") },
    { id: "avisos", selector: marca("avisos") },
    {
      id: "comisiones",
      selector: marca("nav-mis_comisiones", "nav-mas"),
    },
    {
      id: "informes",
      selector: marca("nav-informe", "nav-resumen", "nav-mas"),
    },
    { id: "perfil", selector: marca("usuario", "perfil-movil") },
    { id: "final", ruta: "/" },
  ],
  consultor: [
    { id: "bienvenida", ruta: "/" },
    { id: "inicio", ruta: "/", selector: marca("inicio-cifras") },
    { id: "agenda", ruta: "/agenda", selector: marca("agenda") },
    { id: "preparar" },
    { id: "resultado", ilustracion: "consultoria" },
    { id: "pipeline", ruta: "/pipeline", selector: marca("pipeline-tablero") },
    { id: "horario", selector: marca("usuario", "perfil-movil") },
    { id: "avisos", selector: marca("avisos") },
    {
      id: "comisiones",
      selector: marca("nav-mis_comisiones", "nav-mas"),
    },
    { id: "final", ruta: "/" },
  ],
};

/** El papel que toca a quien entra: el consultor, la suya; el resto, la del setter. */
export const papelDe = (rol: { esConsultor: boolean }): PapelGuia =>
  rol.esConsultor ? "consultor" : "setter";

/**
 * ¿Se le ofrece la guía al entrar? Solo a setters y consultores (no a Jan y Robert), una vez, y
 * nunca en un navegador automatizado (las pruebas), para no tapar la pantalla.
 */
export const ofrecerAlEntrar = ({
  esAdmin,
  cargado,
  vista,
  automatizado,
}: {
  esAdmin: boolean;
  cargado: boolean;
  vista: boolean;
  automatizado: boolean;
}) => cargado && !esAdmin && !vista && !automatizado;
